/**
 * 포맷 유틸리티
 *
 * 파일 크기, 날짜/시간 포맷 등 여러 컴포넌트에서 중복 정의되던 포맷 함수들을 통합.
 */

// ─── 파일 크기 ─────────────────────────────────────────────────────────────

/** 바이트를 B/KB/MB/GB 형식으로 포맷 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0KB';
  if (!bytes) return '-';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(i === 0 ? 0 : 2)) + ' ' + sizes[i];
};

// ─── 날짜/시간 ─────────────────────────────────────────────────────────────

/** 날짜 포맷: YYYY년 M월 D일 HH:mm */
export const formatDateTime = (ts: number): string => {
  const d = new Date(ts);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** 날짜 포맷 (요일 포함): YYYY년 M월 D일(요일) */
export const formatMeetingDate = (ts: number): string => {
  if (!ts) return '';
  const d = new Date(ts);
  const days = ['일', '월', '화', '수', '목', '금', '토'];
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일(${days[d.getDay()]})`;
};

/** 밀리초를 H:MM:SS 형식으로 포맷 */
export const formatDuration = (ms: number): string => {
  if (!ms || ms < 0) return '0:00:00';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/** 12시간제 시간 포맷: 오전/오후 H:MM */
export const formatTime12h = (ts: number): string => {
  const d = new Date(ts);
  let h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, '0');
  const ampm = h < 12 ? '오전' : '오후';
  h = h % 12 || 12;
  return `${ampm} ${h}:${m}`;
};

/** 날짜 문자열을 YYYY-MM-DD HH:mm:ss 형식으로 포맷 */
export const formatDateString = (dateStr: string): string => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const sec = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${sec}`;
};

// ─── 사용자 이름 ───────────────────────────────────────────────────────────

/**
 * 사용자 이름을 언어에 따라 포맷팅
 * - 한글: 3글자 이상이면 첫 글자 제거 (예: "김철수" → "철수")
 * - 영어: 첫 글자만 대문자로 (예: "john" → "J")
 */
export const formatUserName = (name: string): string => {
  if (!name) return '';
  const firstChar = name.charAt(0);
  const isKorean = /[가-힣]/.test(firstChar);
  if (isKorean) {
    return name.length >= 3 ? name.substring(1) : name;
  }
  return name.charAt(0).toUpperCase();
};

// ─── 이메일 ────────────────────────────────────────────────────────────────

/** 이메일 유효성 검사 */
export const isValidEmail = (str: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);

/** 이메일 마스킹 (4글자 이후 별표) */
export const maskEmail = (email: string): string => {
  const atIdx = email.indexOf('@');
  if (atIdx <= 0) return email;
  const local = email.substring(0, atIdx);
  const domain = email.substring(atIdx);
  const visible = local.substring(0, Math.min(4, local.length));
  return `${visible}${'*'.repeat(Math.max(0, local.length - visible.length))}${domain}`;
};

// ─── 전화번호 ──────────────────────────────────────────────────────────────

/** 전화번호 하이픈 포맷 (숫자만 있는 경우 자동 삽입) */
export const formatPhoneNumber = (phone?: string | null): string | undefined => {
  if (!phone) return undefined;
  if (phone.includes('-')) return phone;
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('02')) {
    if (digits.length === 9) return digits.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
    if (digits.length === 10) return digits.replace(/(\d{2})(\d{4})(\d{4})/, '$1-$2-$3');
  }
  if (digits.length === 10) return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  if (digits.length === 11) return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
  return phone;
};
